<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import FileSelectDropzone from '@/components/ui/file-upload/FileSelectDropzone.vue';
import ActionFeedback from '@/components/workspace/ActionFeedback.vue';
import { formatDate } from '~/lib/dateFormat';
import type { UploadedFile } from '~/types/models';

interface Props {
    entityType: UploadedFile['entityType'];
    entityId: string;
    title?: string;
    emptyText?: string;
    showExistingFiles?: boolean;
    showTitle?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    title: 'Files',
    emptyText: 'No files uploaded yet.',
    showExistingFiles: true,
    showTitle: true,
});

const filesStore = useFilesStore();

const selectedFiles = ref<File[]>([]);
const uploading = ref(false);
const localError = ref('');
const uploadStatus = ref<'idle' | 'loading' | 'success' | 'error'>('idle');
const lastUploadCount = ref(0);

const entityKey = computed(() => `${props.entityType}:${props.entityId}`);
const files = computed(() => filesStore.filesByEntity[entityKey.value] || []);
const loading = computed(() => filesStore.loadingByEntity[entityKey.value] || false);

function prettySize(bytes?: number): string {
    if (!bytes || bytes <= 0) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function queueFiles(filesList: FileList | File[] | null | undefined) {
    if (!filesList) return;
    selectedFiles.value = Array.from(filesList).filter((file) => file.size > 0);
    localError.value = '';
    uploadStatus.value = 'idle';
}

function clearSelection() {
    selectedFiles.value = [];
}

async function loadFiles() {
    if (!props.entityId) return;
    localError.value = '';
    await filesStore.fetchFiles(props.entityType, props.entityId);
}

async function uploadSelected() {
    if (selectedFiles.value.length === 0) return;

    localError.value = '';
    uploading.value = true;
    uploadStatus.value = 'loading';
    const uploadCount = selectedFiles.value.length;

    try {
        for (const file of selectedFiles.value) {
            await filesStore.uploadFile(props.entityType, props.entityId, file);
        }
        lastUploadCount.value = uploadCount;
        clearSelection();
        uploadStatus.value = 'success';
    } catch (error: any) {
        localError.value = error.message || 'Failed to upload files.';
        uploadStatus.value = 'error';
    } finally {
        uploading.value = false;
    }
}

async function removeFile(file: UploadedFile) {
    localError.value = '';
    try {
        await filesStore.deleteFile(props.entityType, props.entityId, file);
    } catch (error: any) {
        localError.value = error.message || 'Failed to delete file.';
    }
}

watch(() => [props.entityType, props.entityId], () => {
    loadFiles();
}, { immediate: true });
</script>

<template>
    <div class="space-y-4">
        <div v-if="showTitle || showExistingFiles" class="flex items-center justify-between">
            <h3 v-if="showTitle" class="text-base font-semibold">{{ title }}</h3>
            <Badge v-if="showExistingFiles" variant="outline">{{ files.length }} files</Badge>
        </div>

        <FileSelectDropzone
            title="Drag and drop files here"
            description="You can also browse and upload multiple files in one go."
            button-label="Browse files"
            @files-selected="queueFiles"
        >
            <div v-if="selectedFiles.length > 0" class="space-y-3 rounded-lg border bg-card p-4">
                <div class="flex flex-wrap items-center justify-between gap-2">
                    <p class="text-sm font-medium text-foreground">
                        Ready to upload {{ selectedFiles.length }} {{ selectedFiles.length === 1 ? 'file' : 'files' }}
                    </p>
                    <div class="flex items-center gap-2">
                        <Button type="button" size="sm" variant="ghost" @click="clearSelection">Clear</Button>
                        <Button type="button" size="sm" :disabled="uploading" @click="uploadSelected">
                            {{ uploading ? 'Uploading...' : `Upload ${selectedFiles.length} ${selectedFiles.length === 1 ? 'file' : 'files'}` }}
                        </Button>
                    </div>
                </div>

                <div class="grid gap-2 sm:grid-cols-2">
                    <div
                        v-for="file in selectedFiles"
                        :key="`${file.name}-${file.size}-${file.lastModified}`"
                        class="rounded-md border px-3 py-2 text-sm"
                    >
                        <p class="truncate font-medium text-foreground">{{ file.name }}</p>
                        <p class="text-xs text-muted-foreground">{{ prettySize(file.size) }}</p>
                    </div>
                </div>
            </div>
        </FileSelectDropzone>

        <ActionFeedback
            :status="localError || filesStore.filesError ? 'error' : uploadStatus"
            :title="localError || filesStore.filesError
                ? 'Upload could not be completed'
                : uploadStatus === 'loading'
                    ? 'Uploading files…'
                    : `${lastUploadCount} ${lastUploadCount === 1 ? 'file is' : 'files are'} ready`"
            :description="localError || filesStore.filesError || (uploadStatus === 'success' ? 'The upload is available to the team in this workspace.' : 'Keep this page open while the upload finishes.')"
            @retry="uploadSelected"
        />

        <div v-if="showExistingFiles && loading" class="text-sm text-muted-foreground">Loading files...</div>

        <div v-else-if="showExistingFiles && files.length === 0" class="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
            {{ emptyText }}
        </div>

        <div v-else-if="showExistingFiles" class="space-y-2">
            <div
                v-for="file in files"
                :key="file.id"
                class="flex items-center justify-between gap-3 rounded-lg border p-3"
            >
                <div class="min-w-0">
                    <a
                        :href="file.fileURL"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="block truncate text-sm font-medium underline-offset-4 hover:underline"
                    >
                        {{ file.fileName }}
                    </a>
                    <p class="text-xs text-muted-foreground">
                        {{ prettySize(file.sizeBytes) }} • {{ formatDate(file.createdAt, 'datetime') }}
                    </p>
                </div>
                <Button variant="ghost" size="sm" class="text-muted-foreground hover:text-destructive" @click="removeFile(file)">
                    Remove
                </Button>
            </div>
        </div>
    </div>
</template>
